import { SET_LAB_FEATURE_FLAGS } from 'actions/actionConstants'
import { trackClientEvent } from 'analytics/analytics'
import { registerLereExperimentSeed } from 'api/lab'
import { getLereDynamicPricingGroupFromLabFlags } from 'checkout/lib/utils/lereDynamicPricing'
import { LabFeatureFlags } from 'constants/lab'
import { getMemberIdForStitchedSeedLookup } from 'lib/lab/labDistinctId'
import type { AppDispatch } from 'src/client/store'
import { canUseTrackingIdentityOnClient } from '../../consent/waitingForConsent'
import type { LereDynamicPricingGroup } from './types'
import { isLereSeedRegistrationPending } from './isLereSeedRegistrationPending'
import { getLereDynamicPricingExperimentStatus, getLereMemberEligibilityFlag, isLereMemberExcluded } from './lereDynamicPricingExperimentEntry'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { isLereDynamicPricingExcluded } from './lereDynamicPricingEligibility'
import { omitKeys } from 'lib/object/objectUtils'

export function isLereIdentityCurrent(state: App.State, memberId?: string): boolean {
  const currentMemberId = getMemberIdForStitchedSeedLookup(state)
  return currentMemberId === memberId && canUseTrackingIdentityOnClient() && !isLereDynamicPricingExcluded(state)
}

export function storeLereDynamicPricingFlag(dispatch: AppDispatch, flag: App.LabFeatureFlagResult): void {
  dispatch({
    type: SET_LAB_FEATURE_FLAGS,
    labFlags: { [LabFeatureFlags.enableLereDynamicPricingGroup]: flag },
  })
}

export function trackLereDynamicPricingExposure(group: LereDynamicPricingGroup, distinctId: string, state: App.State): void {
  if (!canUseTrackingIdentityOnClient() || isLereDynamicPricingExcluded(state)) return
  const memberId = getMemberIdForStitchedSeedLookup(state)
  if (isLereMemberExcluded(state.system.features.labFlags, memberId)) return
  trackClientEvent({
    subject: 'determined_lere_dynamic_group',
    action: `${distinctId}|${memberId || 'unknown'}`,
    category: group,
    type: 'nonInteraction',
  })
}

export async function registerLereExposureIdentity(distinctId: string): Promise<void> {
  try {
    await registerLereExperimentSeed(distinctId)
  } catch(error) {
    // The seed can still be syncing; this expected conflict does not affect the visible price.
    if (isLereSeedRegistrationPending(error)) return
    // A failed registration must not replace the price already shown to this device.
    console.error('Failed to register LERE experiment seed:', error)
  }
}

/** All client LERE decisions pass this gate, including force-refresh and direct Lab requests. */
export async function ensureLereMemberEligibility(dispatch: AppDispatch, getState: () => App.State): Promise<boolean> {
  if (!canUseTrackingIdentityOnClient()) return false
  const state = getState()
  if (isLereDynamicPricingExcluded(state)) return false
  const memberId = getMemberIdForStitchedSeedLookup(state)
  if (!memberId) return true
  const flag = state.system.features.labFlags[LabFeatureFlags.enableLereDynamicPricingGroup]
  if (isLereMemberExcluded(state.system.features.labFlags, memberId)) return false
  if (flag?.eligibilityCheckedMemberId === memberId) return true

  try {
    const status = await getLereDynamicPricingExperimentStatus(memberId, state.auth.domainUserId ?? getDomainUserId())
    // An unavailable status cannot authorize a known member's first entry.
    if (!status || !isLereIdentityCurrent(getState(), memberId)) return false
    const eligibilityFlag = getLereMemberEligibilityFlag(status, memberId, getState().system.features.labFlags[LabFeatureFlags.enableLereDynamicPricingGroup])
    if (eligibilityFlag) storeLereDynamicPricingFlag(dispatch, eligibilityFlag)
    return !isLereMemberExcluded(getState().system.features.labFlags, memberId)
  } catch(error) {
    console.error('Failed to check LERE experiment eligibility:', error)
    return false
  }
}

interface RecordLereDecisionOptions {
  labFlags: App.LabFlags
  distinctId: string
  requestState: App.State
  requestMemberId?: string
  dispatch: AppDispatch
  getState: () => App.State
}

export async function recordLereDynamicPricingDecision({
  labFlags,
  distinctId,
  requestState,
  requestMemberId,
  dispatch,
  getState,
}: RecordLereDecisionOptions): Promise<App.LabFlags> {
  const latestState = getState()
  if (isLereDynamicPricingExcluded(latestState)) {
    return omitKeys([LabFeatureFlags.enableLereDynamicPricingGroup], labFlags)
  }
  if (isLereMemberExcluded(latestState.system.features.labFlags, getMemberIdForStitchedSeedLookup(latestState))) {
    return { ...labFlags, [LabFeatureFlags.enableLereDynamicPricingGroup]: latestState.system.features.labFlags[LabFeatureFlags.enableLereDynamicPricingGroup] }
  }
  const group = getLereDynamicPricingGroupFromLabFlags(labFlags)
  if (!group) return labFlags

  const flagKey = LabFeatureFlags.enableLereDynamicPricingGroup
  if (!isLereIdentityCurrent(getState(), requestMemberId)) {
    // Keep unrelated flags, but discard the pricing decision from an obsolete identity.
    return Object.fromEntries(Object.entries(labFlags).filter(([key]) => key !== flagKey))
  }

  const useDomainUserId = requestState.system.features.labFlags[flagKey]?.useDomainUserId
  const flag = { ...labFlags[flagKey], eligibilityCheckedMemberId: latestState.system.features.labFlags[flagKey]?.eligibilityCheckedMemberId, assignmentDistinctId: distinctId, useDomainUserId }
  // Make the exposure available to login reconciliation while registration is in flight.
  storeLereDynamicPricingFlag(dispatch, flag)
  trackLereDynamicPricingExposure(group, distinctId, getState())

  if (requestState.auth.account?.memberId && !useDomainUserId) {
    // Register identified first exposures before another device can claim the mapping.
    await registerLereExposureIdentity(distinctId)
  }

  if (isLereDynamicPricingExcluded(getState())) {
    return omitKeys([flagKey], labFlags)
  }

  // Login may have updated the flag while registration was in flight; retain that update.
  return { ...labFlags, [flagKey]: getState().system.features.labFlags[flagKey] }
}
